import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';
import './Auth.css';

export const TERMS_URL = 'https://app.termly.io/document/terms-of-service/fdbb78f0-13a4-4e26-8308-1615683c9865';
export const PRIVACY_URL = 'https://app.termly.io/document/privacy-policy/0dcb12c9-7703-4262-8b93-37cfc29a2ecc';

export default function AuthLayout({ children, title, subtitle, mode = 'login', steps, footer, centered = false, onboarding = false }) {
  const { t } = useTranslation('auth');
  const { language, changeLanguage, isRTL } = useLanguage();
  useEffect(() => {
    const previous = document.title;
    document.title = `${title} | JOBSEEQR`;
    return () => { document.title = previous; };
  }, [title]);
  return (
    <div className={`auth-page${centered ? ' auth-page-centered' : ''}${onboarding ? ' auth-page-onboarding' : ''}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <section className="auth-main" aria-labelledby="auth-title">
        <header className="auth-header">
          <Link className="auth-brand" to="/" aria-label="JOBSEEQR" dir="ltr">JOBS<span>EEQR</span></Link>
          {!centered && <div className="auth-language" role="group" aria-label={t('shared.language')} dir="ltr">
            <button type="button" lang="en" aria-pressed={language === 'en'} onClick={() => changeLanguage('en')}>English</button>
            <button type="button" lang="ar" aria-pressed={language === 'ar'} onClick={() => changeLanguage('ar')}>العربية</button>
          </div>}
        </header>
        <div className={onboarding ? 'auth-onboarding-stage' : 'auth-stage'}>
        {onboarding && <div className="auth-onboarding-progress">
          {steps}
          <p className="auth-eyebrow">{t('shared.forEmployers')}</p>
        </div>}
        <main className="auth-content">
          {!onboarding && <>{steps}<p className="auth-eyebrow">{t('shared.forEmployers')}</p></>}
          <h1 id="auth-title">{title}</h1>
          {subtitle && <p className="auth-subtitle">{subtitle}</p>}
          {children}
          {footer && <div className="auth-footer">{footer}</div>}
        </main>
        </div>
        <footer className="auth-legal">
          <span dir="ltr">© {new Date().getFullYear()} JOBSEEQR</span>
          <div><a href={TERMS_URL} target="_blank" rel="noreferrer">{t('signup.termsOfService')}</a><a href={PRIVACY_URL} target="_blank" rel="noreferrer">{t('signup.privacyPolicy')}</a></div>
        </footer>
      </section>
      {!centered && <aside style={{ backgroundImage: 'url(/bg-color.png)' }} className="auth-story" aria-label={t('shared.about')}>
        <div className="auth-story-copy">
          <p className="auth-eyebrow">{t('shared.hiringAcrossMena')}</p>
          <h2>{t(`${mode === 'login' ? 'login' : 'signup'}.tagline`)}</h2>
          <p>{t(`${mode === 'login' ? 'login' : 'signup'}.cta`)}</p>
          <div className="auth-story-rule" />
          <p className="auth-story-note">{t('shared.workspace')}</p>
        </div>
        <span className="auth-story-brand" dir="ltr">JOBSEEQR</span>
      </aside>}
    </div>
  );
}

export function AuthNotice({ children, success = false }) {
  if (!children) return null;
  return <div className={`auth-notice ${success ? 'auth-notice-success' : ''}`} role={success ? 'status' : 'alert'}>{children}</div>;
}

export function SetupSteps({ current }) {
  const { t } = useTranslation('auth');
  return <ol className="auth-steps" aria-label={t('setup.progress')}>
    {['company', 'profile'].map((key, index) => <li key={key} aria-current={index === current ? 'step' : undefined} data-complete={index < current}>
      <span className="auth-step-number" aria-hidden="true">{index < current ? <i className="fa-solid fa-check" /> : index + 1}</span>
      <span>{t(`setup.steps.${key}`)}</span>
    </li>)}
  </ol>;
}
